import { styled, FloraVariants, ComponentCSSProp } from '@grupoboticario/flora-react-system'
import {
  ChevronRightIcon,
  HouseIcon,
  ThreeDotsHorizontalIcon,
} from '@grupoboticario/flora-react-icons'
import React from 'react'
import { Flex } from '@grupoboticario/flora-react-flex'
import { Hidden } from '@grupoboticario/flora-react-hidden'
import { Box } from '@grupoboticario/flora-react-box'

const NewIconDots = styled(ThreeDotsHorizontalIcon)
const NewIconHome = styled(HouseIcon)

const StyledBreadcrumb = styled('ol', {
  display: 'flex',
  padding: 0,
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '$bodySmall',
  fontFamily: '$secondary',

  '& > li:not(:first-child):not(:nth-last-child(-n+2))': {
    display: 'none',
    '@tablet': {
      display: 'flex',
    },
  },

  variants: {
    variant: {
      standard: {
        li: {
          color: '$nonInteractivePredominant',
          a: {
            color: '$linkDefault',
            '&:hover': {
              color: '$linkHover',
              textDecoration: 'underline',
            },
          },
          div: {
            color: '$nonInteractiveAuxiliar',
          },
          [`${NewIconDots}`]: {
            color: '$linkDefault',
            '&:hover': {
              color: '$linkHover',
            },
          },
        },
      },
      alt: {
        li: {
          color: '$nonInteractiveAltPredominant',
          span: {
            fontSize: 'bodySmall',
          },
          a: {
            color: '$linkAlt',
            '&:hover': {
              color: '$linkAltHover',
              textDecoration: 'underline',
            },
          },
          div: {
            color: '$nonInteractiveAltAuxiliar',
          },
          [`${NewIconDots}`]: {
            color: '$linkAlt',
            '&:hover': {
              color: '$linkAltHover',
              textDecoration: 'underline',
            },
          },
        },
      },
    },
  },

  defaultVariants: {
    variant: 'standard',
  },
})

const BreadcrumbItem = styled('li', {
  display: 'flex',
  alignItems: 'center',
  truncationWidth: 'number',
  whiteSpace: 'nowrap',
  fontSize: '$bodySmall',
  fontFamily: '$secondary',
})

type Navigation = {
  text: string
  url: string
  icon?: React.ReactElement
  as?: React.ReactNode
}

export type BreadcrumbProps = React.ComponentProps<typeof StyledBreadcrumb> &
  ComponentCSSProp &
  FloraVariants<typeof StyledBreadcrumb> & {
    navigation: Navigation[]
  }

export const Breadcrumb = ({ navigation, ...rest }: BreadcrumbProps) => {
  return (
    <Box as="nav" aria-label="Breadcrumb">
      <StyledBreadcrumb data-flora="breadcrumb" {...rest}>
        {navigation?.map((nav: Navigation, index) => {
          const as = (nav.as ?? 'a') as any
          if (index === 0) {
            return (
              <Flex key={`${nav.text}-${nav.url}`} align="center" as="li">
                <BreadcrumbItem as={as} href={nav.url}>
                  <NewIconHome size={20} title="Início" titleId="breadcrumb-id" />
                </BreadcrumbItem>
                <ChevronRightIcon />
              </Flex>
            )
          }
          return index < navigation.length - 1 ? (
            <Flex key={`${nav.text}-${nav.url}`} align="center" as="li">
              <Hidden from="tablet">
                <BreadcrumbItem as={as} href={nav.url} aria-label={nav.text}>
                  <NewIconDots />
                </BreadcrumbItem>
              </Hidden>
              <Hidden only={['mobile']}>
                <BreadcrumbItem as={as} href={nav.url}>
                  {nav.text}
                </BreadcrumbItem>
              </Hidden>
              <ChevronRightIcon />
            </Flex>
          ) : (
            <BreadcrumbItem key={`${nav.text}-${nav.url}`} as="li" aria-current="page">
              {nav.text}
            </BreadcrumbItem>
          )
        })}
      </StyledBreadcrumb>
    </Box>
  )
}
